import React, { useState, useEffect } from "react";
import { Header } from "@tanstack/react-table";
import { DateRangePicker, DatePicker } from "@mantine/dates";
import { Switch, Text } from "@mantine/core";

interface DateFilterProps<T> {
  header: Header<T, any>;
  enableModeToggle?: boolean;
  defaultMode?: 'single' | 'range';
  placeholder?: {
    single?: string;
    range?: string;
  };
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  styles?: {
    container?: React.CSSProperties;
  };
}

export const DateFilter = <T,>({
  header,
  enableModeToggle = true,
  defaultMode = 'single',
  placeholder = {
    single: 'Pick date',
    range: 'Pick date range'
  },
  size = 'xs',
  styles = {}
}: DateFilterProps<T>) => {
  const [isRange, setIsRange] = useState(defaultMode === 'range');

  // Check if current filter represents a single date (same start and end dates)
  const isSingleDateFilter = () => {
    const filterValue = header.column.getFilterValue();
    if (!Array.isArray(filterValue) || filterValue.length !== 2) return false;
    
    const [startStr, endStr] = filterValue;
    if (!startStr || !endStr) return false;
    
    // Extract date parts from JST strings
    const startDate = startStr.split('T')[0];
    const endDate = endStr.split('T')[0];
    
    // Check if it's the same date with start=00:00:00 and end=23:59:59
    return startDate === endDate && 
           startStr.includes('T00:00:00+09:00') && 
           endStr.includes('T23:59:59+09:00');
  };

  // Set initial mode based on existing filter and update when filter changes
  useEffect(() => {
    if (isSingleDateFilter()) {
      setIsRange(false);
    } else {
      const filterValue = header.column.getFilterValue();
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        setIsRange(true);
      }
    }
  }, [header.column.getFilterValue()]);

  const formatDateToJST = (date: Date, isEndOfDay = false) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    if (isEndOfDay) {
      return `${year}-${month}-${day}T23:59:59+09:00`;
    }
    return `${year}-${month}-${day}T00:00:00+09:00`;
  };

  const handleSingleDateChange = (date: Date | null) => {
    if (date) {
      const startDate = formatDateToJST(date);
      const endDate = formatDateToJST(date, true);
      
      header.column.setFilterValue([startDate, endDate]);
    } else {
      header.column.setFilterValue(null);
    }
  };

  const handleRangeChange = (dates: [Date | null, Date | null]) => {
    if (dates[0] && dates[1]) {
      header.column.setFilterValue([
        formatDateToJST(dates[0]),
        formatDateToJST(dates[1], true)
      ]);
    } else if (!dates[0] && !dates[1]) {
      header.column.setFilterValue(null);
    }
  };

  const handleModeToggle = (checked: boolean) => {
    setIsRange(checked);
    header.column.setFilterValue(null);
  };

  const containerStyle: React.CSSProperties = {
    minWidth: 200,
    padding: '8px',
    ...styles.container
  };

  const inputStyles = {
    input: {
      fontSize: '12px',
      minHeight: '28px',
    }
  };

  return (
    <div style={containerStyle}>
      {enableModeToggle && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
          <Switch
            checked={isRange}
            onChange={(event) => handleModeToggle(event.currentTarget.checked)}
            size={size}
          />
          <Text size={size} color="dimmed">
            {isRange ? 'Date Range' : 'Single Date'}
          </Text>
        </div>
      )}
      
      {isRange ? (
        <DateRangePicker
          type="range"
          placeholder={placeholder.range}
          onChange={handleRangeChange}
          size={size}
          withinPortal
          styles={inputStyles}
        />
      ) : (
        <DatePicker
          placeholder={placeholder.single}
          onChange={handleSingleDateChange}
          size={size}
          withinPortal
          styles={inputStyles}
        />
      )}
    </div>
  );
};